import { FlightsFareTypes } from 'constants/flight'
import {
  queryKeyAdults,
  queryKeyCheckIn,
  queryKeyCheckOut,
  queryKeyChildren,
  queryKeyDepartDate,
  queryKeyFareType,
  queryKeyInfants,
  queryKeyReturnDate,
} from 'constants/url'
import { TRANSFER_SEARCH_VERTICAL } from 'constants/experience'

export type TransferOfferTripType = App.ExperienceTransferType | 'ROUND-TRIP'

export type TransferOfferSearchDefaults = {
  fareType?: FlightsFareTypes
  preferredTransferType?: TransferOfferTripType
  arrivalDate?: string
  departureDate?: string
  travellers: App.ExperienceTransferView['travellers']
}

const TRANSFER_OFFER_QUERY_KEYS = [
  queryKeyFareType,
  queryKeyCheckIn,
  queryKeyCheckOut,
  queryKeyDepartDate,
  queryKeyReturnDate,
  queryKeyAdults,
  queryKeyChildren,
  queryKeyInfants,
] as const

function parsePositiveInt(value: string | null, fallback: number): number {
  if (value === null || value === '') return fallback
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback
}

function hasTransferOfferSearchFields(searchParams: URLSearchParams): boolean {
  return TRANSFER_OFFER_QUERY_KEYS.some((key) => searchParams.has(key))
}

export function isTransferSearchParams(searchParams: URLSearchParams): boolean {
  return searchParams.get('vertical') === TRANSFER_SEARCH_VERTICAL
}

export function appendTransferOfferSearchParams(
  target: URLSearchParams,
  searchParams: URLSearchParams,
): URLSearchParams {
  if (!isTransferSearchParams(searchParams)) {
    return target
  }

  TRANSFER_OFFER_QUERY_KEYS.forEach((key) => {
    const value = searchParams.get(key)
    if (value) {
      target.set(key, value)
    }
  })

  return target
}

export function getPreferredTransferTypeFromFareType(
  fareType: string | null | undefined,
): TransferOfferTripType | undefined {
  if (fareType === FlightsFareTypes.ONE_WAY) {
    return 'AIRPORT-TO-HOTEL'
  }
  if (fareType === FlightsFareTypes.RETURN) {
    return 'ROUND-TRIP'
  }
  return undefined
}

export function parseTransferOfferSearchDefaults(
  searchParams: URLSearchParams,
): TransferOfferSearchDefaults | undefined {
  if (!hasTransferOfferSearchFields(searchParams)) {
    return undefined
  }

  const fareTypeValue = searchParams.get(queryKeyFareType)
  const fareType =
    fareTypeValue === FlightsFareTypes.ONE_WAY || fareTypeValue === FlightsFareTypes.RETURN
      ? fareTypeValue
      : undefined

  const arrivalDate =
    searchParams.get(queryKeyCheckIn) ||
    searchParams.get(queryKeyDepartDate) ||
    undefined
  const departureDate =
    searchParams.get(queryKeyCheckOut) ||
    searchParams.get(queryKeyReturnDate) ||
    undefined

  const adults = parsePositiveInt(searchParams.get(queryKeyAdults), 2)
  const children = parsePositiveInt(searchParams.get(queryKeyChildren), 0)
  const infants = parsePositiveInt(searchParams.get(queryKeyInfants), 0)

  return {
    fareType,
    preferredTransferType: getPreferredTransferTypeFromFareType(fareType),
    arrivalDate,
    departureDate: fareType === FlightsFareTypes.ONE_WAY ? undefined : departureDate,
    travellers: {
      adults,
      // Offer form only supports adults/children; infants still count as passengers.
      children: children + infants,
      childSeats: 0,
    },
  }
}
